<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title>
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          Add a New Order Item
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="6">
              <v-autocomplete
                ref="order"
                outlined
                v-model="form.order_id"
                :items="orders"
                item-text="order_number"
                item-value="id"
                label="Order *"
                prepend-inner-icon="mdi-package-variant"
                clearable
                :error-messages="errors.order_id"
                @change="onOrderChange"
              >
                <template v-slot:item="{ item }">
                  <div class="d-flex flex-column">
                    <div class="font-weight-bold">
                      {{ item.order_number }}
                    </div>
                    <div class="text-caption">
                      Customer: {{ item.patient ? item.patient.name : 'N/A' }}
                    </div>
                    <div class="text-caption">
                      Total: ${{ parseFloat(item.final_amount || 0).toFixed(2) }}
                    </div>
                  </div>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                ref="product"
                outlined
                v-model="form.product_id"
                :items="products"
                item-text="name"
                item-value="id"
                label="Product *"
                prepend-inner-icon="mdi-cube-outline"
                clearable
                :error-messages="errors.product_id"
                @change="onProductChange"
              >
                <template v-slot:item="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-3">
                      <v-img
                        :src="item.image_path || '/images/default-product.png'"
                        :alt="item.name"
                      />
                    </v-avatar>
                    <div class="d-flex flex-column">
                      <div class="font-weight-bold">{{ item.name }}</div>
                      <div class="text-caption">
                        Price: ${{ parseFloat(item.price || 0).toFixed(2) }}
                      </div>
                    </div>
                  </div>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model.number="form.quantity"
                outlined
                label="Quantity *"
                prepend-inner-icon="mdi-numeric"
                :error-messages="errors.quantity"
                type="number"
                min="1"
                @input="calculateTotal"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model.number="form.product_price"
                outlined
                label="Product Price *"
                prepend-inner-icon="mdi-currency-inr"
                :error-messages="errors.product_price"
                type="number"
                step="0.01"
                min="0"
                @input="calculateTotal"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="totalPrice"
                outlined
                label="Total Price"
                prepend-inner-icon="mdi-currency-inr"
                readonly
                type="number"
                step="0.01"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-switch
                class="mt-0 ml-5"
                v-model="form.is_active"
                inset
                :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
              ></v-switch>
            </v-col>
          </v-row>

          <!-- Order Information Section -->
          <v-card v-if="selectedOrder" class="mb-4 mt-4" outlined>
            <v-card-title>
              <v-icon left color="primary">mdi-receipt</v-icon>
              Order Information
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col sm="6">
                  <div class="info-item">
                    <strong>Order Number:</strong> {{ selectedOrder.order_number }}
                  </div>
                  <div class="info-item">
                    <strong>Customer:</strong> {{ selectedOrder.patient ? selectedOrder.patient.name : 'N/A' }}
                  </div>
                  <div class="info-item">
                    <strong>Status:</strong>
                    <v-chip small :color="selectedOrder.order_status ? 'success' : 'warning'" text-color="white">
                      {{ selectedOrder.order_status ? 'Processed' : 'Pending' }}
                    </v-chip>
                  </div>
                </v-col>
                <v-col sm="6">
                  <div class="info-item">
                    <strong>Total Amount:</strong> ${{ parseFloat(selectedOrder.total_amount || 0).toFixed(2) }}
                  </div>
                  <div class="info-item">
                    <strong>Tax Amount:</strong> ${{ parseFloat(selectedOrder.tax_amount || 0).toFixed(2) }}
                  </div>
                  <div class="info-item">
                    <strong>Final Amount:</strong> ${{ parseFloat(selectedOrder.final_amount || 0).toFixed(2) }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Product Information Section -->
          <v-card v-if="selectedProduct" class="mb-4" outlined>
            <v-card-title>
              <v-icon left color="success">mdi-package-variant</v-icon>
              Product Information
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col sm="4">
                  <v-img
                    :src="selectedProduct.image_path || '/images/default-product.png'"
                    :alt="selectedProduct.name"
                    height="150"
                    contain
                  />
                </v-col>
                <v-col sm="8">
                  <div class="info-item">
                    <strong>Name:</strong> {{ selectedProduct.name }}
                  </div>
                  <div class="info-item">
                    <strong>Price:</strong> ${{ parseFloat(selectedProduct.price || 0).toFixed(2) }}
                  </div>
                  <div class="info-item">
                    <strong>Description:</strong>
                    {{ selectedProduct.description || 'No description available' }}
                  </div>
                  <div class="info-item">
                    <strong>Status:</strong>
                    <v-chip small :color="selectedProduct.is_active ? 'success' : 'error'" text-color="white">
                      {{ selectedProduct.is_active ? 'Active' : 'Inactive' }}
                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-divider class="mt-5 mb-5"></v-divider>
          <v-btn
            :loading="isLoading"
            :disabled="isLoading"
            rounded
            color="primary"
            dark
            @click="save"
          >
            Save Order Item
          </v-btn>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "OrderItemCreate",
  data() {
    return {
      form: {
        order_id: null,
        product_id: null,
        quantity: 1,
        product_price: 0,
        is_active: true,
      },
      orders: [],
      products: [],
      selectedOrder: null,
      selectedProduct: null,
      isLoading: false,
      errors: {},
    };
  },
  computed: {
    totalPrice() {
      return (this.form.quantity * this.form.product_price).toFixed(2);
    },
  },
  mounted() {
    this.getMasters();
  },
  methods: {
    async getMasters() {
      try {
        const response = await axios.get("/masters?context=order_items");
        if (response.data) {
          this.orders = response.data.orders || [];
          this.products = response.data.products || [];
        }
      } catch (error) {
        console.error("Error loading masters:", error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },
    onOrderChange() {
      this.selectedOrder = this.orders.find(
        (order) => order.id === this.form.order_id
      );
    },
    onProductChange() {
      this.selectedProduct = this.products.find(
        (product) => product.id === this.form.product_id
      );
      if (this.selectedProduct) {
        this.form.product_price = parseFloat(this.selectedProduct.price || 0);
      }
    },
    calculateTotal() {
      // Total is automatically calculated in the computed property
    },
    async save() {
      try {
        this.isLoading = true;
        this.errors = {};
        
        const formData = {
          ...this.form,
          quantity: parseInt(this.form.quantity) || 1,
          product_price: parseFloat(this.form.product_price) || 0,
        };
        
        await axios.post("/order_items", formData);
        this.isLoading = false;
        this.$router.push("/order-items");
      } catch (error) {
        this.isLoading = false;
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        } else {
          console.error("Error saving order item:", error);
        }
      }
    },
  },
};
</script>

<style scoped>
.info-item {
  margin-bottom: 8px;
}
</style>